/**
 * globalLoadingSlice - Redux slice for global loading state
 *
 * Manages a global loading overlay for critical operations like:
 * - Payment processing
 * - Order creation
 * - Large file uploads
 * - Authentication flows
 *
 * Features:
 * - Single source of truth for global loading state
 * - Custom loading messages
 * - Prevents user interaction during critical operations
 *
 * @module store/slices/globalLoadingSlice
 */

import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {Object} GlobalLoadingState
 * @property {boolean} is_loading - Whether global loading is active
 * @property {string|null} loading_message - Custom message to display (e.g., "Procesando pago...")
 */

const initialState = {
  is_loading: false,
  loading_message: null,
};

export const globalLoadingSlice = createSlice({
  name: 'global_loading',
  initialState,
  reducers: {
    /**
     * Show global loading overlay
     * @param {GlobalLoadingState} state
     * @param {Object} action
     * @param {string} [action.payload] - Optional custom loading message
     */
    showLoading: (state, action) => {
      state.is_loading = true;
      state.loading_message = action.payload || null;
    },

    /**
     * Hide global loading overlay
     * @param {GlobalLoadingState} state
     */
    hideLoading: (state) => {
      state.is_loading = false;
      state.loading_message = null;
    },

    /**
     * Update loading message without changing loading state
     * @param {GlobalLoadingState} state
     * @param {Object} action
     * @param {string} action.payload - New loading message
     */
    setLoadingMessage: (state, action) => {
      state.loading_message = action.payload;
    },
  },
});

export const { showLoading, hideLoading, setLoadingMessage } = globalLoadingSlice.actions;

/**
 * Selectors
 */
export const selectIsLoading = (state) => state.global_loading.is_loading;
export const selectLoadingMessage = (state) => state.global_loading.loading_message;

export default globalLoadingSlice.reducer;
